/**
 * Release Notes Store Contracts (SCH-2488)
 *
 * The payloads the backend's `RELEASE_NOTES_SLACK_POST` job sends the ops
 * store (`apps/ops-store/convex/releases.ts`, routes in `http.ts`) under a
 * `backend` credential holding `releases:write`, and the store's answers. The
 * backend parses every answer against these; the store's internal mutation
 * validators refuse a body that does not match (an unknown field included).
 *
 *   POST /releases/config  → the job's settings, or null until Ruben sets them
 *   POST /releases/claim   → claim one release before posting it
 *   POST /releases/settle  → record the post, or its failure
 */

import { z } from 'zod'

/** A Slack channel id as `chat.postMessage` addresses one: `C…`, `G…` or `D…`. */
const SlackChannelIdSchema = z.string().regex(/^[CGD][A-Z0-9]{6,}$/)

/** A post row's state. Only a `failed` release may be claimed again. */
export const ReleaseSlackPostStatusSchema = z.enum(['claimed', 'posted', 'failed'])
export type ReleaseSlackPostStatus = z.infer<typeof ReleaseSlackPostStatusSchema>

/** The `releases.slack` settings, with `pipelines` defaulted by the store. */
export const ReleasesSlackConfigSchema = z
  .object({
    enabled: z.boolean(),
    channelId: SlackChannelIdSchema,
    /** Linear release pipeline NAMES, matched exactly. */
    pipelines: z.array(z.string().min(1)).min(1),
    /** Releases created before this instant are never posted. */
    postReleasesCreatedAfter: z.string().datetime()
  })
  .strict()
export type ReleasesSlackConfig = z.infer<typeof ReleasesSlackConfigSchema>

/** `POST /releases/config`: an empty object. */
export const ReleasesConfigRequestSchema = z.object({}).strict()
export type ReleasesConfigRequest = z.infer<typeof ReleasesConfigRequestSchema>

export const ReleasesConfigResponseSchema = z.object({ config: ReleasesSlackConfigSchema.nullable() }).strict()
export type ReleasesConfigResponse = z.infer<typeof ReleasesConfigResponseSchema>

/** `POST /releases/claim`: one Linear release, before its Slack post. */
export const ReleaseClaimRequestSchema = z
  .object({
    linearReleaseId: z.string().min(1),
    releaseName: z.string().min(1),
    pipelineName: z.string().min(1),
    channelId: SlackChannelIdSchema
  })
  .strict()
export type ReleaseClaimRequest = z.infer<typeof ReleaseClaimRequestSchema>

/** `claimed: false` names the state of the row that refused the claim. */
export const ReleaseClaimResponseSchema = z.discriminatedUnion('claimed', [
  z.object({ claimed: z.literal(true) }).strict(),
  z.object({ claimed: z.literal(false), status: ReleaseSlackPostStatusSchema.exclude(['failed']) }).strict()
])
export type ReleaseClaimResponse = z.infer<typeof ReleaseClaimResponseSchema>

/** `POST /releases/settle`: the claimed release was posted (with Slack's `ts`), or the post failed. */
export const ReleaseSettleRequestSchema = z.discriminatedUnion('status', [
  z
    .object({
      linearReleaseId: z.string().min(1),
      status: z.literal('posted'),
      slackTs: z.string().nullable()
    })
    .strict(),
  z
    .object({
      linearReleaseId: z.string().min(1),
      status: z.literal('failed'),
      lastError: z.string().min(1)
    })
    .strict()
])
export type ReleaseSettleRequest = z.infer<typeof ReleaseSettleRequestSchema>

/** `settled: false` when the release was not `claimed`; nothing changed. */
export const ReleaseSettleResponseSchema = z.object({ settled: z.boolean() }).strict()
export type ReleaseSettleResponse = z.infer<typeof ReleaseSettleResponseSchema>
